import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import {
  ActionBar,
  DataGrid,
  DetailPane,
  SplitView,
  StatusBar,
  type DesktopAction,
  type DetailTab,
  type GridColumn,
} from '@/components/desktop';
import { HistoryPanel } from '@/components/documents';
import { DraftNotice } from '@/components/DraftNotice';
import { ActionDialog, type ActionField } from '@/components/hr/ActionDialog';
import { useDraft } from '@/stores/drafts';
import { StatePill } from '@/components/hr/StatePill';
import { stateLabel } from '@/components/hr/states';
import { printTable } from '@/components/hrPrint';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  OkNote,
  Pager,
  Select,
  Table,
  TextArea,
  errorMessage,
  money,
  shortDate,
  todayIso,
} from '@/components/ui';
import {
  PAY_METHOD_TEXT,
  employeeLabel,
  hrGet,
  monthName,
  useEmployeeOptions,
  useHrInvalidation,
} from '@/hooks/hr';
import { useAuth } from '@/stores/auth';
import { farmScopeQuery, useFarmScope } from '@/stores/farmScope';
import { useScreenIntent } from '@/stores/shellNav';

const PAGE_SIZE = 100;

type State = 'PENDING' | 'APPROVED' | 'ACTIVE' | 'CLOSED' | 'REJECTED' | 'REVERSED';

interface AdvanceRow {
  id: string;
  documentNo: string;
  kind: 'ADVANCE' | 'LOAN';
  employeeId: string;
  employeeCode: string;
  employeeName: string;
  farmCode: string | null;
  issuedOn: string;
  advanceAmount?: string | null;
  instalments: number;
  instalmentAmount?: string | null;
  recoverFrom: string;
  reason: string | null;
  status: string;
  approval: string;
  state: State;
  decisionNote: string | null;
  paidOutAt: string | null;
  payMethod: 'CASH' | 'BANK' | 'WALLET' | null;
  payReference: string | null;
  recoveredAmount?: string | null;
  writtenOffAmount?: string | null;
  outstandingAmount?: string | null;
  reversalReason: string | null;
  raisedByYou: boolean;
  version: number;
}

interface AdvanceDetail extends AdvanceRow {
  recoveries: Array<{
    id: string;
    source: 'PAYROLL' | 'CASH' | 'WRITE_OFF' | 'SETTLEMENT';
    recoveryAmount?: string | null;
    recoveredOn: string;
    reference: string | null;
    note: string | null;
  }>;
  skips: Array<{ year: number; month: number; reason: string }>;
  schedule: Array<{ month: string; instalmentAmount?: string | null }>;
}

const SOURCE_TEXT = {
  PAYROLL: 'Taken from pay',
  CASH: 'Repaid in cash',
  WRITE_OFF: 'Written off',
  SETTLEMENT: 'Taken from final settlement',
};

const FILTERS: Array<{ value: string; label: string }> = [
  { value: 'PENDING', label: 'Waiting for a decision' },
  { value: 'APPROVED', label: 'Approved — to pay out' },
  { value: 'ACTIVE', label: 'Being recovered' },
  { value: 'CLOSED', label: 'Repaid' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'REVERSED', label: 'Reversed' },
];

type Dialog =
  | { kind: 'approve' | 'reject' | 'payOut' | 'repay' | 'skip' | 'writeOff' | 'reverse'; row: AdvanceRow }
  | null;

function kindText(kind: 'ADVANCE' | 'LOAN') {
  return kind === 'LOAN' ? 'Loan' : 'Advance';
}

function ymLabel(ym: string) {
  const [y, m] = ym.split('-').map(Number);
  return `${monthName(m!)} ${y}`;
}

/**
 * Advances & Loans: raised by one person, approved by another, then paid
 * out. After payout, payroll recovers it month by month; cash repayments,
 * skipped months, write-offs and reversals are recorded here.
 */
export function Advances() {
  const intent = useScreenIntent('hr-adv');
  const { can } = useAuth();
  const invalidate = useHrInvalidation();
  const farmIds = useFarmScope((s) => s.selectedFarmIds);
  const [state, setState] = useState<string>('');
  const [kind, setKind] = useState<string>('');
  const [skip, setSkip] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(intent?.recordId ?? null);
  const [tab, setTab] = useState('details');
  const [form, setForm] = useState<{ mode: 'create' } | { mode: 'edit'; row: AdvanceRow } | null>(() =>
    intent?.action === 'create' ? { mode: 'create' } : null,
  );
  const [dialog, setDialog] = useState<Dialog>(null);
  const [note, setNote] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['hr', 'advances', farmIds, state, kind, skip, intent?.employeeId],
    queryFn: () =>
      hrGet<{ total: number; outstandingAmount?: string; items: AdvanceRow[] }>('/api/v1/hr/advances', {
        ...farmScopeQuery(farmIds),
        state,
        kind,
        employeeId: intent?.employeeId,
        skip,
        take: PAGE_SIZE,
      }),
  });
  const rows = list.data?.items ?? [];
  const selected = rows.find((r) => r.id === selectedKey) ?? null;

  const detail = useQuery({
    queryKey: ['hr', 'advances', 'detail', selectedKey],
    queryFn: () => hrGet<AdvanceDetail>(`/api/v1/hr/advances/${selectedKey}`),
    enabled: selectedKey !== null,
  });

  const remove = useMutation({
    mutationFn: (row: AdvanceRow) => api.delete(`/api/v1/hr/advances/${row.id}?version=${row.version}`),
    onSuccess: (_, row) => {
      invalidate();
      setSelectedKey(null);
      setNote(`${row.documentNo} deleted.`);
    },
    onError: (e) => setProblem(errorMessage(e, 'Could not delete it')),
  });

  function done(message: string) {
    invalidate();
    setProblem(null);
    setNote(message);
  }

  function printStatement(d: AdvanceDetail) {
    printTable({
      title: `${kindText(d.kind)} ${d.documentNo} — ${d.employeeCode} ${d.employeeName}`,
      subtitle: `Given ${shortDate(d.issuedOn)} · ${money(d.advanceAmount)} · ${d.instalments} × ${money(d.instalmentAmount)} from ${ymLabel(d.recoverFrom.slice(0, 7))} · Still owed ${money(d.outstandingAmount)}`,
      columns: [
        { key: 'recoveredOn', header: 'Date', kind: 'date' },
        { key: 'source', header: 'How' },
        { key: 'reference', header: 'Reference' },
        { key: 'recoveryAmount', header: 'Amount', kind: 'money' },
      ],
      rows: d.recoveries.map((r) => ({ ...r, source: SOURCE_TEXT[r.source] })),
      signatures: ['Employee', 'Accounts'],
    });
  }

  function rowActions(row: AdvanceRow): DesktopAction[] {
    const waiting = row.state === 'PENDING';
    const active = row.state === 'ACTIVE';
    return [
      {
        label: 'Edit',
        icon: 'edit',
        shortcut: 'F2',
        onSelect: () => setForm({ mode: 'edit', row }),
        allowed: can('hr.advance.update'),
        enabled: waiting,
        reason: 'Only one still waiting for a decision can be changed',
      },
      {
        label: 'Approve',
        icon: 'approve',
        onSelect: () => setDialog({ kind: 'approve', row }),
        allowed: can('hr.advance.approve'),
        enabled: waiting && !row.raisedByYou,
        reason: row.raisedByYou ? 'Whoever raised it cannot approve it' : 'Only one waiting for a decision can be approved',
        separatorBefore: true,
      },
      {
        label: 'Reject',
        icon: 'reject',
        onSelect: () => setDialog({ kind: 'reject', row }),
        allowed: can('hr.advance.reject'),
        enabled: waiting && !row.raisedByYou,
        reason: row.raisedByYou ? 'Whoever raised it cannot reject it' : 'Only one waiting for a decision can be rejected',
      },
      {
        label: 'Pay out',
        icon: 'cash',
        onSelect: () => setDialog({ kind: 'payOut', row }),
        allowed: can('hr.advance.post'),
        enabled: row.state === 'APPROVED',
        reason: 'It must be approved and not yet paid out',
      },
      {
        label: 'Cash repayment',
        icon: 'cash',
        onSelect: () => setDialog({ kind: 'repay', row }),
        allowed: can('hr.advance.update'),
        enabled: active,
        reason: 'Only one being recovered can be repaid',
        separatorBefore: true,
      },
      {
        label: 'Skip a month',
        icon: 'month',
        onSelect: () => setDialog({ kind: 'skip', row }),
        allowed: can('hr.advance.approve'),
        enabled: active || row.state === 'APPROVED',
        reason: 'Only one being recovered can skip a month',
      },
      {
        label: 'Write off the rest',
        icon: 'retire',
        danger: true,
        onSelect: () => setDialog({ kind: 'writeOff', row }),
        allowed: can('hr.writeoff.post'),
        enabled: active,
        reason: 'Only one being recovered can be written off',
      },
      {
        label: 'Reverse',
        icon: 'reverse',
        danger: true,
        onSelect: () => setDialog({ kind: 'reverse', row }),
        allowed: can('hr.advance.reverse'),
        enabled: active && Number(row.recoveredAmount ?? 0) === 0 && Number(row.writtenOffAmount ?? 0) === 0,
        reason: 'Only a payout nothing has been recovered against can be reversed',
      },
      {
        label: 'Delete',
        icon: 'remove',
        danger: true,
        shortcut: 'Del',
        onSelect: () => {
          if (window.confirm(`Delete ${row.documentNo}? It has not been decided yet, so nothing else changes.`)) {
            remove.mutate(row);
          }
        },
        allowed: can('hr.advance.delete'),
        enabled: waiting,
        reason: 'Only one still waiting for a decision can be deleted',
      },
      {
        label: 'Print statement',
        icon: 'print',
        onSelect: () => {
          void hrGet<AdvanceDetail>(`/api/v1/hr/advances/${row.id}`).then(printStatement);
        },
        separatorBefore: true,
      },
    ];
  }

  const toolbar: DesktopAction[] = [
    {
      label: 'Give advance or loan',
      icon: 'add',
      primary: true,
      shortcut: 'Ins',
      onSelect: () => setForm({ mode: 'create' }),
      allowed: can('hr.advance.create'),
    },
    ...(selected
      ? rowActions(selected)
      : [
          {
            label: 'Approve',
            icon: 'approve' as const,
            onSelect: () => {},
            allowed: can('hr.advance.approve'),
            enabled: false,
            reason: 'Choose one first',
            separatorBefore: true,
          },
        ]),
  ];

  const columns: Array<GridColumn<AdvanceRow>> = [
    {
      key: 'doc',
      header: 'Number',
      width: 150,
      render: (r) => (
        <span className="withicon">
          <AppIcon kind={r.kind === 'LOAN' ? 'loan' : 'advance'} size={14} />
          <span className="mono">{r.documentNo}</span>
        </span>
      ),
    },
    { key: 'kind', header: 'Kind', width: 80, render: (r) => kindText(r.kind) },
    { key: 'person', header: 'Person', width: 210, render: (r) => `${r.employeeCode} — ${r.employeeName}` },
    { key: 'farm', header: 'Farm', width: 80, render: (r) => <span className="dim">{r.farmCode ?? 'Head office'}</span> },
    { key: 'given', header: 'Given', width: 100, render: (r) => shortDate(r.issuedOn) },
    { key: 'amount', header: 'Amount', align: 'end', width: 110, render: (r) => money(r.advanceAmount) },
    { key: 'each', header: 'Each month', align: 'end', width: 110, render: (r) => money(r.instalmentAmount) },
    { key: 'owed', header: 'Still owed', align: 'end', width: 110, render: (r) => (r.outstandingAmount ? money(r.outstandingAmount) : '—') },
    { key: 'state', header: 'State', width: 180, render: (r) => <StatePill state={r.state} /> },
  ];

  if (form) {
    return (
      <div className="workbench">
        <div className="workbench__main workbench__main--form">
          <AdvanceForm
            key={form.mode === 'edit' ? form.row.id : 'create'}
            editing={form.mode === 'edit' ? form.row : undefined}
            presetEmployeeId={intent?.employeeId}
            onCancel={() => setForm(null)}
            onSaved={(message, id) => {
              setForm(null);
              if (id) setSelectedKey(id);
              done(message);
            }}
          />
        </div>
      </div>
    );
  }

  const d = detail.data && detail.data.id === selectedKey ? detail.data : null;
  const tabs: DetailTab[] = d
    ? [
        {
          id: 'details',
          label: 'Details',
          icon: 'details',
          content: (
            <div className="stack">
              <dl className="detaillist">
                <dt>Person</dt>
                <dd>{d.employeeCode} — {d.employeeName}</dd>
                <dt>Kind</dt>
                <dd>{kindText(d.kind)}</dd>
                <dt>State</dt>
                <dd><StatePill state={d.state} /></dd>
                <dt>Given on</dt>
                <dd>{shortDate(d.issuedOn)}</dd>
                <dt>Amount</dt>
                <dd>{money(d.advanceAmount)}</dd>
                <dt>Recovered</dt>
                <dd>{d.instalments === 1 ? 'In one go' : `${d.instalments} months`} at {money(d.instalmentAmount)}, from {ymLabel(d.recoverFrom.slice(0, 7))}</dd>
                <dt>Why</dt>
                <dd>{d.reason ?? '—'}</dd>
                {d.paidOutAt && (
                  <>
                    <dt>Paid out</dt>
                    <dd>
                      {shortDate(d.paidOutAt)}
                      {d.payMethod ? ` · ${PAY_METHOD_TEXT[d.payMethod]}` : ''}
                      {d.payReference ? ` · ${d.payReference}` : ''}
                    </dd>
                  </>
                )}
                {d.decisionNote && (
                  <>
                    <dt>Decision note</dt>
                    <dd>{d.decisionNote}</dd>
                  </>
                )}
                {d.outstandingAmount && (
                  <>
                    <dt>Still owed</dt>
                    <dd><strong>{money(d.outstandingAmount)}</strong></dd>
                  </>
                )}
                {d.reversalReason && (
                  <>
                    <dt>Reversed because</dt>
                    <dd>{d.reversalReason}</dd>
                  </>
                )}
              </dl>
              {d.schedule.length > 0 && (
                <>
                  <h3 className="formsection">Still to come</h3>
                  <Table
                    count={d.schedule.length}
                    empty="Nothing left to recover."
                    head={
                      <tr>
                        <th>Month</th>
                        <th className="num">To recover</th>
                      </tr>
                    }
                  >
                    {d.schedule.map((s) => (
                      <tr key={s.month}>
                        <td>{ymLabel(s.month)}</td>
                        <td className="num">{money(s.instalmentAmount)}</td>
                      </tr>
                    ))}
                  </Table>
                </>
              )}
              {d.skips.length > 0 && (
                <p className="muted">
                  Skipped: {d.skips.map((s) => `${monthName(s.month)} ${s.year} (${s.reason})`).join('; ')}
                </p>
              )}
            </div>
          ),
        },
        {
          id: 'recovered',
          label: 'Recovered',
          icon: 'cash',
          content: (
            <Table
              count={d.recoveries.length}
              empty="Nothing recovered yet."
              head={
                <tr>
                  <th>Date</th>
                  <th>How</th>
                  <th>Reference</th>
                  <th className="num">Amount</th>
                </tr>
              }
            >
              {d.recoveries.map((r) => (
                <tr key={r.id}>
                  <td>{shortDate(r.recoveredOn)}</td>
                  <td>{SOURCE_TEXT[r.source]}</td>
                  <td className="dim">{r.reference ?? r.note ?? '—'}</td>
                  <td className="num">{money(r.recoveryAmount)}</td>
                </tr>
              ))}
            </Table>
          ),
        },
        { id: 'history', label: 'History', icon: 'history', content: <HistoryPanel path={`/api/v1/hr/advances/${d.id}/history`} /> },
      ]
    : [];

  const pending = rows.filter((r) => r.state === 'PENDING').length;

  return (
    <div className="workbench">
      <SplitView
        id="hr-advances"
        direction="column"
        initial={420}
        firstLabel="advances list"
        first={
          <div className="workbench__main">
            <div className="filterbar">
              <Select
                label="Show"
                value={state}
                onChange={(v) => {
                  setState(v);
                  setSkip(0);
                }}
                options={FILTERS}
                placeholder="Everything"
              />
              <Select
                label="Kind"
                value={kind}
                onChange={(v) => {
                  setKind(v);
                  setSkip(0);
                }}
                options={[
                  { value: 'ADVANCE', label: 'Advances' },
                  { value: 'LOAN', label: 'Loans' },
                ]}
                placeholder="Both"
              />
            </div>
            <ActionBar actions={toolbar} />
            {note && <OkNote message={note} />}
            {problem && <ErrorNote message={problem} />}
            {list.error && <ErrorNote message={list.error.message} />}
            <DataGrid
              id="hr-advances"
              label="Advances and loans"
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              selectedKey={selectedKey}
              onSelect={(k) => {
                setSelectedKey(k);
                setNote(null);
              }}
              onOpen={(r) => {
                setSelectedKey(r.id);
                setTab('details');
              }}
              onEdit={(r) => {
                if (r.state === 'PENDING' && can('hr.advance.update')) setForm({ mode: 'edit', row: r });
              }}
              onDelete={(r) => {
                if (r.state === 'PENDING' && can('hr.advance.delete') && window.confirm(`Delete ${r.documentNo}?`)) remove.mutate(r);
              }}
              actions={rowActions}
              loading={list.isPending}
              empty={state ? `Nothing ${stateLabel(state).toLowerCase()}.` : 'No advances or loans yet.'}
            />
            <Pager skip={skip} take={PAGE_SIZE} total={list.data?.total ?? 0} onChange={setSkip} />
          </div>
        }
        second={
          <DetailPane
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            empty="Choose an advance or loan to see what has been recovered and what is still to come."
            title={
              selected && (
                <span className="withicon">
                  <AppIcon kind={selected.kind === 'LOAN' ? 'loan' : 'advance'} size={14} />
                  <span className="mono">{selected.documentNo}</span> {selected.employeeName}
                </span>
              )
            }
          />
        }
      />
      <StatusBar>
        <span>{list.data?.total ?? 0} shown</span>
        {pending > 0 && <span>{pending} waiting for a decision</span>}
        {list.data?.outstandingAmount && <span>Still owed in all: {money(list.data.outstandingAmount)}</span>}
        <span>{selected ? `Chosen: ${selected.documentNo}` : 'Nothing chosen'}</span>
      </StatusBar>

      {dialog && <AdvanceDialog dialog={dialog} onClose={() => setDialog(null)} onDone={done} />}
    </div>
  );
}

/** The dialog behind each action that needs a little more than a click. */
function AdvanceDialog({
  dialog,
  onClose,
  onDone,
}: {
  dialog: NonNullable<Dialog>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { row } = dialog;
  const base = `/api/v1/hr/advances/${row.id}`;
  const today = todayIso();
  const thisMonth = today.slice(0, 7);

  const configs: Record<
    NonNullable<Dialog>['kind'],
    {
      title: string;
      icon: 'approve' | 'reject' | 'cash' | 'month' | 'retire' | 'reverse';
      intro?: string;
      fields: ActionField[];
      initial?: Record<string, string>;
      submit: string;
      danger?: boolean;
      run: (v: Record<string, string>) => Promise<unknown>;
      message: string;
    }
  > = {
    approve: {
      title: `Approve ${row.documentNo}`,
      icon: 'approve',
      intro: `${row.employeeName} — ${money(row.advanceAmount)}. Once approved, it can be paid out.`,
      fields: [{ key: 'comment', label: 'Comment', type: 'textarea' }],
      submit: 'Approve',
      run: (v) => api.post(`${base}/approve`, { comment: v.comment || undefined }),
      message: `${row.documentNo} approved. It can now be paid out.`,
    },
    reject: {
      title: `Reject ${row.documentNo}`,
      icon: 'reject',
      fields: [{ key: 'comment', label: 'Why', type: 'textarea', required: true }],
      submit: 'Reject',
      danger: true,
      run: (v) => api.post(`${base}/reject`, { comment: v.comment }),
      message: `${row.documentNo} rejected.`,
    },
    payOut: {
      title: `Pay out ${row.documentNo}`,
      icon: 'cash',
      intro: `Record that ${money(row.advanceAmount)} was given to ${row.employeeName}. Payroll starts recovering it from ${ymLabel(row.recoverFrom.slice(0, 7))}.`,
      fields: [
        { key: 'paidOn', label: 'Paid on', type: 'date', required: true },
        {
          key: 'method',
          label: 'How',
          type: 'select',
          required: true,
          options: [
            { value: 'CASH', label: 'Cash' },
            { value: 'BANK', label: 'Bank transfer' },
            { value: 'WALLET', label: 'Mobile wallet' },
          ],
        },
        { key: 'reference', label: 'Voucher, cheque or transfer number' },
      ],
      initial: { paidOn: today, method: 'CASH' },
      submit: 'Record payout',
      run: (v) => api.post(`${base}/pay-out`, { paidOn: v.paidOn, method: v.method, reference: v.reference || undefined }),
      message: `${row.documentNo} paid out. Recovery starts from ${ymLabel(row.recoverFrom.slice(0, 7))}.`,
    },
    repay: {
      title: `Cash repayment — ${row.documentNo}`,
      icon: 'cash',
      intro: `Still owed: ${money(row.outstandingAmount)}.`,
      fields: [
        { key: 'amount', label: 'Amount repaid', type: 'number', required: true },
        { key: 'date', label: 'Date', type: 'date', required: true },
        { key: 'note', label: 'Receipt number or note' },
      ],
      initial: { date: today },
      submit: 'Record repayment',
      run: (v) => api.post(`${base}/repay`, { amount: v.amount, date: v.date, note: v.note || undefined }),
      message: 'Repayment recorded.',
    },
    skip: {
      title: `Skip a month — ${row.documentNo}`,
      icon: 'month',
      intro: 'Nothing is recovered in that month; the rest moves back a month.',
      fields: [
        { key: 'month', label: 'Month', type: 'month', required: true },
        { key: 'reason', label: 'Why', type: 'textarea', required: true },
      ],
      initial: { month: thisMonth },
      submit: 'Skip this month',
      run: (v) => {
        const [year, month] = (v.month ?? '').split('-').map(Number);
        return api.post(`${base}/skip`, { year, month, reason: v.reason });
      },
      message: 'Month skipped.',
    },
    writeOff: {
      title: `Write off ${row.documentNo}`,
      icon: 'retire',
      intro: `${money(row.outstandingAmount)} will no longer be recovered from ${row.employeeName}. This cannot be undone.`,
      fields: [{ key: 'reason', label: 'Why', type: 'textarea', required: true }],
      submit: 'Write off',
      danger: true,
      run: (v) => api.post(`${base}/write-off`, { reason: v.reason }),
      message: `${row.documentNo} written off.`,
    },
    reverse: {
      title: `Reverse ${row.documentNo}`,
      icon: 'reverse',
      intro: 'Use this when the payout was recorded by mistake. Nothing has been recovered yet.',
      fields: [{ key: 'reason', label: 'Why', type: 'textarea', required: true }],
      submit: 'Reverse',
      danger: true,
      run: (v) => api.post(`${base}/reverse`, { reason: v.reason }),
      message: `${row.documentNo} reversed.`,
    },
  };

  const c = configs[dialog.kind];
  return (
    <ActionDialog
      title={c.title}
      icon={c.icon}
      intro={c.intro}
      fields={c.fields}
      {...(c.initial ? { initial: c.initial } : {})}
      submitLabel={c.submit}
      danger={c.danger ?? false}
      onClose={onClose}
      onSubmit={async (v) => {
        await c.run(v);
        onDone(c.message);
      }}
    />
  );
}

function AdvanceForm({
  editing,
  presetEmployeeId,
  onCancel,
  onSaved,
}: {
  editing: AdvanceRow | undefined;
  presetEmployeeId: string | undefined;
  onCancel: () => void;
  onSaved: (message: string, id?: string) => void;
}) {
  const people = useEmployeeOptions('CURRENT', !editing);
  const today = todayIso();
  const nextMonth = (() => {
    const [y, m] = today.split('-').map(Number);
    return m === 12 ? `${y! + 1}-01` : `${y}-${String(m! + 1).padStart(2, '0')}`;
  })();
  const [v, setV] = useState(() => ({
    employeeId: editing?.employeeId ?? presetEmployeeId ?? '',
    kind: editing?.kind ?? 'ADVANCE',
    amount: editing?.advanceAmount ? String(Number(editing.advanceAmount)) : '',
    instalments: String(editing?.instalments ?? 1),
    recoverFrom: editing ? editing.recoverFrom.slice(0, 7) : nextMonth,
    issuedOn: editing?.issuedOn ?? today,
    reason: editing?.reason ?? '',
  }));
  const [problem, setProblem] = useState<string | null>(null);

  // Brought back after a reload, keyed so editing one record never offers
  // another one's typing.
  const [blankValues] = useState(() => v);
  const draft = useDraft(editing ? `advance.edit.${editing.id}` : 'advance.new', v, (saved) => setV(saved));

  function startAgain() {
    setV(blankValues);
    setProblem(null);
  }

  const set = (key: keyof typeof v) => (value: string) => setV((s) => ({ ...s, [key]: value }));

  const amount = Number(v.amount);
  const count = Number(v.instalments);
  const each = amount > 0 && count > 0 ? Math.ceil(amount / count) : 0;

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        amount: v.amount,
        instalments: count,
        recoverFrom: `${v.recoverFrom}-01`,
        issuedOn: v.issuedOn,
        reason: v.reason.trim() || undefined,
      };
      if (editing) {
        await api.patch(`/api/v1/hr/advances/${editing.id}`, { version: editing.version, ...body });
        return { id: editing.id, documentNo: editing.documentNo };
      }
      return api.post<{ id: string; documentNo: string }>('/api/v1/hr/advances', {
        employeeId: v.employeeId,
        kind: v.kind,
        ...body,
      });
    },
    onSuccess: (r) => {
      draft.forget();
      onSaved(
        editing ? `${r.documentNo} saved. It still needs someone else's approval.` : `${r.documentNo} raised. Someone else must approve it before it is paid out.`,
        r.id,
      );
    },
    onError: (e) => setProblem(errorMessage(e, 'Could not save')),
  });

  function submit() {
    if (!v.employeeId) return setProblem('Choose the person.');
    if (!(amount > 0)) return setProblem('Enter an amount above zero.');
    if (!(count >= 1 && count <= 120)) return setProblem('Recover it over 1 to 120 months.');
    if (!v.recoverFrom) return setProblem('Choose the first month to recover from.');
    setProblem(null);
    save.mutate();
  }

  return (
    <FormPanel
      title={editing ? `Change ${editing.documentNo}` : 'Give an advance or loan'}
      icon={<AppIcon kind="advance" />}
      intro="Someone other than you approves it. Once it is paid out, payroll takes the monthly amount from pay until it is repaid."
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DraftNotice draft={draft} onStartAgain={startAgain} />

        <FormGroup title="Who and what" columns={2}>
          {editing ? (
            <Field label="Person" value={`${editing.employeeCode} — ${editing.employeeName}`} onChange={() => {}} disabled />
          ) : (
            <Select
              label="Person"
              required
              value={v.employeeId}
              onChange={set('employeeId')}
              options={(people.data?.items ?? []).map((e) => ({ value: e.id, label: employeeLabel(e) }))}
            />
          )}
          <Select
            label="Kind"
            required
            disabled={!!editing}
            value={v.kind}
            onChange={(k) => setV((s) => ({ ...s, kind: k as 'ADVANCE' | 'LOAN', instalments: k === 'ADVANCE' ? '1' : s.instalments }))}
            options={[
              { value: 'ADVANCE', label: 'Advance against pay' },
              { value: 'LOAN', label: 'Loan repaid over months' },
            ]}
            placeholder=""
          />
          <Field label="Amount" type="number" required value={v.amount} onChange={set('amount')} />
          <Field label="Given on" type="date" required value={v.issuedOn} onChange={set('issuedOn')} />
        </FormGroup>
        <FormGroup title="Paying it back" columns={2} hint={each > 0 ? `About ${money(String(each))} a month.` : undefined}>
          <Field label="Months to recover over" type="number" required min={1} max={120} value={v.instalments} onChange={set('instalments')} />
          <Field label="First month recovered" type="month" required value={v.recoverFrom} onChange={set('recoverFrom')} />
          <TextArea label="Why" value={v.reason} onChange={set('reason')} rows={2} placeholder="e.g. child's school fees" />
        </FormGroup>
        {problem && <ErrorNote message={problem} />}
        <FormActions onCancel={onCancel}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {editing ? 'Save changes' : 'Raise for approval'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}
